type EmptyStateProps = {
  compact?: boolean;
};

const hintPills = ["Widen date range", "Clear filters", "Try another view"];

export function EmptyState({ compact = false }: EmptyStateProps) {
  return (
    <div
      className="remix-empty-state pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden p-4"
      role="status"
      aria-live="polite"
      data-compact={compact ? "true" : "false"}
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-[radial-gradient(circle_at_50%_38%,rgba(59,130,246,0.08),transparent_34%),radial-gradient(circle_at_18%_22%,rgba(16,185,129,0.06),transparent_24%),radial-gradient(circle_at_82%_78%,rgba(249,115,22,0.08),transparent_28%)]"
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.94),rgba(248,250,252,0.7))]"
      />
      <div
        aria-hidden="true"
        className="absolute border border-slate-200/60 bg-[linear-gradient(180deg,rgba(255,255,255,0.78),rgba(248,250,252,0.55))] shadow-[0_18px_60px_rgba(15,23,42,0.06)] backdrop-blur-sm"
        style={{
          inset: compact ? 10 : 14,
          borderRadius: compact ? 20 : 24,
        }}
      />

      <div className="relative z-10 flex max-w-[320px] flex-col items-center gap-4 text-center text-slate-500">
        <div
          className="relative grid place-items-center rounded-full border border-slate-200/70 bg-white/90 shadow-[0_16px_40px_rgba(15,23,42,0.08)] backdrop-blur-sm"
          style={{ width: compact ? 72 : 94, height: compact ? 72 : 94 }}
        >
          <div className="absolute inset-2 rounded-full border border-slate-200/60" />
          <div className="absolute inset-[14px] rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.95),rgba(241,245,249,0.3))]" />
          <svg
            className="relative z-10"
            width={compact ? 36 : 46}
            height={compact ? 36 : 46}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path
              d="M4 6.5h16M4 12h16M4 17.5h16M6.5 4v16M12 4v16M17.5 4v16"
              className="text-slate-300"
              opacity="0.45"
            />
            <path
              d="M4 16.5l4-4 3.25 2.75 3.5-5.25 5.25-2.25"
              className="text-[#34656d]"
              strokeDasharray="24"
              strokeDashoffset="24"
              style={{
                animation:
                  "dash 1.2s ease forwards, pulse 2.6s ease-in-out 1.2s infinite",
              }}
            />
            <circle
              cx="8"
              cy="12.5"
              r="1.1"
              className="fill-current text-[#5cc8ff] opacity-0"
              style={{ animation: "pop 0.45s ease forwards 0.1s" }}
            />
            <circle
              cx="11.4"
              cy="15.1"
              r="1.1"
              className="fill-current text-[#5cc8ff] opacity-0"
              style={{ animation: "pop 0.45s ease forwards 0.2s" }}
            />
            <circle
              cx="14.9"
              cy="9.9"
              r="1.1"
              className="fill-current text-[#ff9f43] opacity-0"
              style={{ animation: "pop 0.45s ease forwards 0.3s" }}
            />
          </svg>
        </div>

        <div className="space-y-1">
          <div className="text-[15px] font-semibold text-slate-700">
            Nothing to show here
          </div>
          <div className="mx-auto max-w-[24ch] text-[12px] leading-5 text-slate-400">
            Try widening the date range or clearing filters to reveal chart
            data.
          </div>
        </div>

        {!compact && (
          <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
            {hintPills.map((pill) => (
              <span
                key={pill}
                className="rounded-full border border-slate-200 bg-white/85 px-3 py-1 text-[11px] font-medium text-slate-500 shadow-sm backdrop-blur-sm"
              >
                {pill}
              </span>
            ))}
          </div>
        )}
      </div>

      <style>{`
        @keyframes dash {
          to { stroke-dashoffset: 0; }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.75; transform: translateY(0); }
          50% { opacity: 1; transform: translateY(-0.5px); }
        }
        @keyframes pop {
          0% { opacity: 0; transform: scale(0.6); }
          100% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
